import { useState } from "react";
import { mapAnswersToEstimatorInput } from "@reno-estimator/ui";
import type { EstimatorResult, ProjectType } from "@reno-estimator/core";
import type { RawAnswerBag } from "@reno-estimator/ui";
import { getEstimateEmailService, isEmailDeliveryConfigured } from "../../engine/emailEstimateClient.js";
import { getAnalyticsClient } from "../../engine/analyticsClient.js";

type SubmitStatus = "idle" | "sending" | "sent" | "invalid_email" | "rate_limited" | "duplicate_request" | "send_failed";

const STATUS_MESSAGE: Partial<Record<SubmitStatus, string>> = {
  sent: "Your estimate has been emailed - check your inbox in the next few minutes.",
  invalid_email: "That doesn't look like a valid email address - please double-check it and try again.",
  rate_limited: "Too many estimate emails have been requested for this address recently - please try again in a few minutes.",
  duplicate_request: "We already emailed this estimate to that address a few minutes ago - check your inbox (and spam folder).",
  send_failed: "We couldn't send that email right now. Please try again shortly."
};

// Audit fix (Phase 23, Full Application Red-Team Audit Finding 1 -
// Critical): with no real EmailTransport configured, the underlying
// service still reports "sent" (ConsoleEmailTransport never fails - it
// only logs locally, see emailEstimateClient.ts), which used to reach the
// homeowner as a false "check your inbox" confirmation. Override just
// that one message when no delivery backend is connected; every other
// status (invalid email, rate limited, ...) is already computed
// client-side and stays accurate regardless of transport.
const NOT_CONFIGURED_SENT_MESSAGE = "This preview has no email delivery backend connected, so nothing was actually emailed.";

/**
 * The Email My Estimate feature (Phase 17). Deliberately thin - all it
 * does is reconstruct the same `EstimatorInput` the visible estimate was
 * computed from (from the current project type + answers, using the same
 * `mapAnswersToEstimatorInput` seam the controller itself uses) and hand
 * it, together with the computed result and the address typed here, to
 * `EstimateEmailService`. No email content, validation, rate-limiting, or
 * duplicate-suppression logic lives in this component - all of that is
 * `@reno-estimator/email`'s job, exactly the same "no business logic in
 * UI components" boundary this app draws for pricing itself.
 */
export function EmailEstimateForm({
  projectType,
  answers,
  result
}: {
  projectType: ProjectType;
  answers: RawAnswerBag;
  result: EstimatorResult;
}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<SubmitStatus>("idle");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");

    const input = mapAnswersToEstimatorInput({ requestId: result.requestId, projectType, answers });
    const service = getEstimateEmailService();
    const sendResult = await service.sendEstimateEmail({ requestId: result.requestId, toEmail: email, input, result });
    setStatus(sendResult.status);
    if (sendResult.status === "sent") {
      getAnalyticsClient().track("email_estimate_requested", { source: "email_estimate_form", projectType }, result.requestId);
    }
  }

  return (
    <div className="estimate-card email-estimate">
      <h2>Email my estimate</h2>
      <p className="email-estimate__help">
        We'll send a summary of this estimate to your inbox for your records - not a formal quote, just a copy of what you
        see here.
      </p>
      {!isEmailDeliveryConfigured() ? (
        <p className="email-estimate__dev-notice" role="note">
          Preview mode: no email delivery backend is connected, so this won't actually send.
        </p>
      ) : null}
      <form className="email-estimate__form" onSubmit={(e) => void handleSubmit(e)}>
        <label htmlFor="email-estimate-address" className="email-estimate__label">
          Email address
        </label>
        <div className="email-estimate__row">
          <input
            id="email-estimate-address"
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status !== "sending") setStatus("idle");
            }}
            placeholder="you@example.com"
            className="email-estimate__input"
            disabled={status === "sending"}
          />
          <button type="submit" className="btn btn--primary" disabled={status === "sending" || email.trim().length === 0}>
            {status === "sending" ? "Sending…" : "Email my estimate"}
          </button>
        </div>
        {status !== "idle" && status !== "sending" ? (
          <p role="status" className={status === "sent" ? "email-estimate__status email-estimate__status--success" : "email-estimate__status email-estimate__status--error"}>
            {status === "sent" && !isEmailDeliveryConfigured() ? NOT_CONFIGURED_SENT_MESSAGE : STATUS_MESSAGE[status]}
          </p>
        ) : null}
      </form>
    </div>
  );
}
